import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { IndividualApprovalsService } from './individual-approvals.service';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import ID from '../../../types/id';
import ConsentRequiredStatus from '../../../interfaces/consent-required-status';
import ConsentRequestApproval from '../../../interfaces/consent-request-approval';
import { IndicatorState } from '../../consent-requests/consent-requests.component';
import { combineLatest, Subscription } from 'rxjs';
import {
  _parseApprovals,
  ApprovalTableItem,
  InvestorApprovalTableItem,
} from '../consent-approvals.component';
import {
  UpdateConsentApprovalTuple,
  UpdateConsentInvestorApprovalsInput,
  UpdateConsentRequestApprovalInput,
} from './graphql/individual-approvals';
import { formatDate } from '@angular/common';
import Participant from "../../../interfaces/participant";

type ApprovalDialogData = {
  consent_request_id: ID;
};

@Component({
    selector: 'app-individual-approvals-dialog',
    templateUrl: './individual-approvals-dialog.component.html',
    styleUrls: ['./individual-approvals-dialog.component.scss'],
    providers: [IndividualApprovalsService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class IndividualApprovalsDialogComponent {
  public approvalItems: ApprovalTableItem[] = [];
  private approvalsSub: Subscription;
  constructor(
    public approvalsService: IndividualApprovalsService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<IndividualApprovalsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApprovalDialogData
  ) {}
  ngOnInit() {
    this.approvalsService.getRequestWithApprovals(this.data.consent_request_id);
    this.approvalsSub = combineLatest({
      approvals: this.approvalsService.consentRequestApprovals$,
      approvalStatuses: this.approvalsService.consentApprovalStatuses$,
      requiredStatuses: this.approvalsService.consentRequiredStatuses$,
    }).subscribe(({ approvals }) => {
      this.approvalItems = [];
      this.approvalItems = _parseApprovals(
        approvals,
        this.approvalsService,
        false
      );
    });
    let sub: Subscription;
    sub = this.dialogRef.afterClosed().subscribe(() => {
      this.ngOnDestroy();
      sub?.unsubscribe();
    });
  }
  private _processInvestorApprovalInputs(
    approvals: InvestorApprovalTableItem[]
  ): UpdateConsentInvestorApprovalsInput[] {
    let investorApprovalInputs: UpdateConsentInvestorApprovalsInput[] = [];
    for (let investorApproval of approvals) {
      let input: UpdateConsentInvestorApprovalsInput = {
        notes: investorApproval.comments,
        consent_approval_status_id:
          investorApproval.approval_status?.id ?? null,
        approval_date: investorApproval.status_date
          ? formatDate(investorApproval.status_date, 'yyyy-MM-dd', 'en-US')
          : null,
        id: investorApproval.investorApproval.id,
      };
      investorApprovalInputs.push(input);
    }
    return investorApprovalInputs;
  }

  public retrieveSponsors(){
    let sponsor = null;
    this.approvalsService.consentRequest$.subscribe((data) => {
      sponsor = data?.project.participants?.find((prtc: Participant) => prtc.role.name == 'Sponsor')?.sponsor ?? null
    })
    return sponsor
  }
  public saveApprovals() {
    let inputs: UpdateConsentApprovalTuple[] = [];
    for (let item of this.approvalItems) {
      let input: UpdateConsentRequestApprovalInput = {
        consent_required_status_id: item.consent_required?.id ?? null,
        investor_report_comment: item.investor_report_comment,
        percent_approval_required: item.percent_required
          ? item.percent_required / 100
          : null,
        investor_sent_date: item.sent_date
          ? formatDate(item.sent_date, 'yyyy-MM-dd', 'en-US')
          : null,
        fund_approval_date: item.fund_approval_date
          ? formatDate(item.fund_approval_date, 'yyyy-MM-dd', 'en-US')
          : null,
        consentRequestInvestorApprovals: {
          update: this._processInvestorApprovalInputs(item.investorApprovals),
        },
      };
      inputs.push([item.approval.id, input]);
    }
    this.approvalsService.updateApprovals(this.data.consent_request_id, inputs);
  }
  ngOnDestroy() {
    this.approvalsSub?.unsubscribe();
    this.approvalsService.tearDown();
  }
}
